import React, { lazy, Suspense, useEffect } from 'react';
import { generatePath, Route, Switch, Link, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, Typography } from '@mui/material';
import routes from '../../../routes';
import SCDashboardAtGlance from './SCDashboardAtGlance';
import SCDashboardLeaderboards from './SCDashboardLeaderboards';
import SCDashboardInsights from './SCDashboardInsights';
import SCDashboardSiteGoals from './SCDashboardSiteGoals';
import { fetchSiteDonorsLocationsIfNeeded, fetchSiteTodosIfNeeded } from '../../../actions/sites';
import useActiveSite from '../../../hooks/useActiveSite';
import SCDashboardEnvironmentImpact from './SCDashboardEnvironmentImpact';
import { Colors } from '../../../assets/theme/Colors';
import SCDashboardSettings from './SCDashboardSettings';
import useHasActiveFeatureFlag from '../../../hooks/useFeatureFlags';
import { FF_SD_DASHBOARD_SETTINGS_PHONE_NUMBERS, FF_SD_DASHBOARD_SETTINGS_SUPPLEMENTAL_REGISTRATION_DOCUMENT } from '../../../components/FeatureFlagWrapper';

const SCDashboardToDo = lazy(() => import('./SCDashboardToDo'));

export const scDashboardPaths = [
  generatePath(routes.dashboardSC),
  generatePath(routes.dashboardSC, { tab: 'at-glance' }),
  generatePath(routes.dashboardSC, { tab: 'leaderboards' }),
  generatePath(routes.dashboardSC, { tab: 'insights' }),
  generatePath(routes.dashboardSC, { tab: 'site-goals' }),
  generatePath(routes.dashboardSC, { tab: 'environment-impact' }),
  generatePath(routes.dashboardSC, { tab: 'settings' }),
];

const Counter = (props) => (
  <Chip
    size="small"
    {...props}
    sx={{
      backgroundColor: '#f7b924',
      fontWeight: 700,
      position: 'absolute',
      scale: 0.8,
      left: 50,
      top: 0,
    }}
  />
);

const SCDashboardDesktopContainer = () => {
  const { url } = useRouteMatch();
  const dispatch = useDispatch();
  const activeSite = useActiveSite();
  const sdTodosEntities = useSelector((state) => state.entities.sites.sdTodos);
  const sdTodos = sdTodosEntities.bySiteId[activeSite.id];
  const unclaimedRescues = sdTodos?.unclaimedRescues || [];
  const totalUnclaimedRescues = unclaimedRescues.length;
  const hasPhoneNumbersFF = useHasActiveFeatureFlag(FF_SD_DASHBOARD_SETTINGS_PHONE_NUMBERS);
  const hasSupplementalRegDocFF = useHasActiveFeatureFlag(FF_SD_DASHBOARD_SETTINGS_SUPPLEMENTAL_REGISTRATION_DOCUMENT);
  const hasSDDashboardSettingsFeatureFlag = hasPhoneNumbersFF || hasSupplementalRegDocFF;

  useEffect(() => {
    dispatch(fetchSiteTodosIfNeeded(activeSite.id));
    dispatch(fetchSiteDonorsLocationsIfNeeded(activeSite.id));
  }, []);

  const scDashboardMenuList = [
    {
      name: 'To Do',
      component: SCDashboardToDo,
      path: generatePath(routes.dashboardSC),
    },
    {
      name: 'At A Glance',
      component: SCDashboardAtGlance,
      path: generatePath(routes.dashboardSC, { tab: 'at-glance' }),
    },
    {
      name: 'Leaderboards',
      component: SCDashboardLeaderboards,
      path: generatePath(routes.dashboardSC, { tab: 'leaderboards' }),
    },
    {
      name: 'Insights',
      component: SCDashboardInsights,
      path: generatePath(routes.dashboardSC, { tab: 'insights' }),
    },
    {
      name: 'Site Goals',
      component: SCDashboardSiteGoals,
      path: generatePath(routes.dashboardSC, { tab: 'site-goals' }),
    },
    {
      name: 'Environmental Impact',
      component: SCDashboardEnvironmentImpact,
      path: generatePath(routes.dashboardSC, { tab: 'environment-impact' }),
    },
    ...(hasSDDashboardSettingsFeatureFlag
      ? [
          {
            name: 'Settings',
            component: SCDashboardSettings,
            path: generatePath(routes.dashboardSC, { tab: 'settings' }),
            isNew: true,
          },
        ]
      : []),
  ];

  const paths = scDashboardMenuList.map((item) => item.path);
  const getActiveTab = () => {
    const path = paths.indexOf(url);

    return Math.max(path, 0);
  };

  return (
    <>
      <Typography variant="h5">My Dashboard</Typography>
      <Box mt={5} display="flex" gap="16px">
        <Box minWidth="max-content">
          {scDashboardMenuList.map((item, index) => (
            <Typography style={{ position: 'relative' }} variant="subtitle2" key={item.name} gutterBottom>
              <Box
                component={Link}
                sx={{
                  textDecoration: 'none',
                  color: Colors.gray,
                  paddingLeft: '10px',
                  '&.active': {
                    color: 'primary.main',
                    position: 'relative',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      left: -3,
                      top: 0,
                      height: '100%',
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                    },
                  },
                }}
                className={index !== getActiveTab() ? '' : 'active'}
                to={item.path}
                key={item.name}
              >
                {item.name}
              </Box>
              {item.isNew && (
                <Chip label="NEW" size="small" color="primary" style={{ height: 16, fontSize: 10, fontWeight: 700, marginLeft: 4, verticalAlign: 'middle' }} />
              )}
              {item.name === 'To Do' && !!totalUnclaimedRescues && <Counter label={totalUnclaimedRescues} />}
            </Typography>
          ))}
        </Box>
        <Box sx={{ width: 'calc(100% - 170px)' }}>
          <Switch>
            <Route
              path={[scDashboardPaths[0], generatePath(routes.index)]}
              exact
              render={() => (
                <Suspense fallback={null}>
                  <SCDashboardToDo />
                </Suspense>
              )}
            />
            <Route path={scDashboardPaths[1]} exact render={() => <SCDashboardAtGlance siteId={activeSite.id} />} />
            <Route path={scDashboardPaths[2]} exact component={SCDashboardLeaderboards} />
            <Route path={scDashboardPaths[3]} exact render={() => <SCDashboardInsights siteId={activeSite.id} />} />
            <Route path={scDashboardPaths[4]} exact render={() => <SCDashboardSiteGoals siteId={activeSite.id} />} />
            <Route
              path={scDashboardPaths[5]}
              exact
              render={() => <SCDashboardEnvironmentImpact siteId={activeSite.id} />}
            />
            <Route
              path={scDashboardPaths[6]}
              exact
              render={() => <SCDashboardSettings siteId={activeSite.id} />}
            />
          </Switch>
        </Box>
      </Box>
    </>
  );
};

export default SCDashboardDesktopContainer;
